import { useOverflowTest } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Tooltip, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useRef } from 'react';
import * as SX from './sx';
import type { SmartTextProps as Props } from './types';

/**
 * ! EXPRIMENTAL COMPONENT
 * usage guidance: use it for long text that may overflow
 * ? the parent element must have a flex display property
 * ? for the text to be truncated, the parent element must have a {min-width: 0} and {display: flex} property
 * ? in some cases of complicated layouts:
 * ?     you need to trace up parents of the component and check if they have a {flex-grow: 1} property,
 * ?     if so, you need to do styling: { minWidth: 0 } for that specific parent element
 * ! this component is in an experimental stage, so if you've faced conditions that it doesn't work, please report it
 */

export const SmartText = memo(
  ({ className, tooltipProps, children, sx, ...props }: Props) => {
    const textRef = useRef<HTMLElement>(null);
    const isTextOverflowing = useOverflowTest(textRef);

    return (
      <Tooltip
        title={isTextOverflowing ? (children as string) : ''}
        enterDelay={tooltipProps?.enterDelay ?? 500}
        {...tooltipProps}
      >
        <Typography
          className={classnames('SmartText', className)}
          sx={mergeSx(SX.root, sx)}
          {...props}
          ref={textRef}
        >
          {children}
        </Typography>
      </Tooltip>
    );
  }
);

SmartText.displayName = 'SmartText';
